import { useState } from 'react';
import { Playlist, PlaylistSong } from '../../../types/PlaylistTypes';
import { 
    createPlaylist as apiCreatePlaylist,
    updatePlaylistMetadata,
    updatePlaylistSongs,
    deletePlaylist as apiDeletePlaylist,
    fetchPlaylistDetails
} from '../utils/playlistApi';

interface CreatePlaylistData {
    name: string;
    description: string;
    is_public: boolean;
    song_ids: number[];
    userId: number;
    theme: string;
}

interface UpdatePlaylistData {
    name: string;
    description: string;
    is_public: boolean;
    theme: string;
    songs: PlaylistSong[];
}

export const usePlaylistOperations = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createPlaylist = async (data: CreatePlaylistData): Promise<Playlist> => {
        setIsLoading(true);
        setError(null);
        try {
            const playlist = await apiCreatePlaylist(data);
            return playlist;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to create playlist";
            setError(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updatePlaylist = async (playlistId: string, data: UpdatePlaylistData): Promise<Playlist> => {
        setIsLoading(true);
        setError(null);
        try {
            // Update metadata first
            const updatedPlaylist = await updatePlaylistMetadata(playlistId, {
                name: data.name,
                description: data.description,
                is_public: data.is_public,
                theme: data.theme
            });

            // Then update songs
            await updatePlaylistSongs(playlistId, data.songs);

            return updatedPlaylist;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to update playlist";
            setError(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const deletePlaylist = async (playlistId: string): Promise<void> => {
        setIsLoading(true);
        setError(null);
        try {
            await apiDeletePlaylist(playlistId);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to delete playlist";
            setError(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const loadPlaylistDetails = async (playlistId: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await fetchPlaylistDetails(playlistId);
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to load playlist";
            setError(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const clearError = () => {
        setError(null);
    };

    return {
        createPlaylist,
        updatePlaylist,
        deletePlaylist,
        loadPlaylistDetails,
        isLoading,
        error,
        clearError
    };
}; 